import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUser, FaSignOutAlt, FaCog, FaTimes, FaSun, FaMoon } from 'react-icons/fa';
import { useAuth } from '@contexts/AuthContext';
import { useTheme } from '@theme/useTheme';
import AutoLoginSettings from '@components/ui/AutoLoginSettings';
import Spinner from '@components/ui/Spinner';

interface ProfileModalProps {
  visible: boolean;
  onClose: () => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ visible, onClose }) => {
  const { user, logout } = useAuth();
  const { colors, mode, toggleMode } = useTheme();
  const [showSettings, setShowSettings] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [visible]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      onClose();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };



  return (
    <>
      {visible && (
        <motion.div
          key="main-profile-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 flex items-center justify-center p-4 z-50"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(2px)'
          }}
          onClick={handleOverlayClick}
        >
          <motion.div
            key="main-profile-content"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="w-full max-w-md p-6 shadow-2xl border-2 rounded-tr-3xl rounded-bl-3xl relative"
            style={{
              backgroundColor: colors.background,
              borderColor: colors.border
            }}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full transition-colors duration-200"
              style={{
                backgroundColor: colors.error + '20',
                color: colors.error
              }}
              aria-label="Close modal"
            >
              <FaTimes size={20} />
            </button>
            {/* Profile Header */}
            <div className="text-center mb-6">
              <div
                className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center overflow-hidden border-2"
                style={{
                  backgroundColor: colors.secondaryButton,
                  borderColor: colors.primary
                }}
              >
                <FaUser size={24} style={{ color: colors.text }} />
              </div>
              <h2
                className="text-xl font-bold mb-1"
                style={{ color: colors.heading }}
              >
                {user?.email?.split('@')[0] || 'User'}
              </h2>
              <p
                className="text-sm"
                style={{ color: colors.text }}
              >
                {user?.email}
              </p>
              {user?.auth_provider && user.auth_provider !== 'email' && (
                <p
                  className="text-xs mt-1"
                  style={{ color: colors.text }}
                >
                  Signed in with {user.auth_provider.charAt(0).toUpperCase() + user.auth_provider.slice(1)}
                </p>
              )}
            </div>

            {/* Profile Actions */}
            <div className="space-y-3">
              {/* Theme Toggle Button */}
              <button
                onClick={toggleMode}
                className="w-full flex items-center justify-center py-3 px-4 rounded-xl border-2 transition-colors duration-200"
                style={{
                  borderColor: colors.primary,
                  color: colors.primary,
                  backgroundColor: colors.secondaryButton
                }}
              >
                {mode === 'light' ? (
                  <>
                    <FaMoon className="mr-2" />
                    Dark Mode
                  </>
                ) : (
                  <>
                    <FaSun className="mr-2" />
                    Light Mode
                  </>
                )}
              </button>

              <button
                onClick={() => setShowSettings(true)}
                className="w-full flex items-center justify-center py-3 px-4 rounded-xl border-2 transition-colors duration-200"
                style={{
                  borderColor: colors.primary,
                  color: colors.primary,
                  backgroundColor: colors.secondaryButton
                }}
              >
                <FaCog className="mr-2" />
                Settings
              </button>

              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="w-full flex items-center justify-center py-3 px-4 rounded-xl border-2 transition-colors duration-200"
                style={{
                  borderColor: colors.error,
                  color: colors.error,
                  backgroundColor: colors.error + '20',
                  opacity: isLoggingOut ? 0.7 : 1
                }}
              >
                {isLoggingOut ? (
                  <Spinner size="sm" color={colors.primary} text="Signing Out..." showText={true} usePrimaryColor={false} />
                ) : (
                  <>
                    <FaSignOutAlt className="mr-2" />
                    Sign Out
                  </>
                )}
              </button>
            </div>

            {/* Account Information Notice */}
            <div
              className="mt-6 p-3 rounded-lg border"
              style={{
                borderColor: colors.border,
                backgroundColor: colors.input
              }}
            >
              <h3 className="text-sm font-semibold mb-2" style={{ color: colors.heading }}>
                Account Information
              </h3>
              <div className="text-xs space-y-1" style={{ color: colors.text }}>
                <p>Profile information cannot be edited at this time.</p>
                <p>Only basic account settings are available.</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            key="settings-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center p-4 z-60"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(2px)'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowSettings(false);
              }
            }}
          >
            <motion.div
              key="settings-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-md p-6 shadow-2xl border-2 rounded-tr-3xl rounded-bl-3xl relative"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border
              }}
            >
              {/* Close Button */}
              <button
                onClick={() => setShowSettings(false)}
                className="absolute top-4 right-4 p-2 rounded-full transition-colors duration-200"
                style={{
                  backgroundColor: colors.error + '20',
                  color: colors.error
                }}
                aria-label="Close modal"
              >
                <FaTimes size={20} />
              </button>
              <AutoLoginSettings onClose={() => setShowSettings(false)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ProfileModal;
